import React from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {getSearchParameters} from 'modules/sapiSearch/selectors/searchParameters'
import {HotelType} from 'types/Hotel'
import {generateAccommodationPageURL} from 'utils/url'

import {Button} from '@vio/atlas/Button'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {PopUp} from '@vio/atlas/PopUp'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {getAnonymousId} from '@vio/core/src/_web/anonymousId'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {navigateTo} from '@vio/core/src/_web/utils/browser/navigateTo'
import {trackProviderRedirect} from '@vio/core/src/analytics/modules/actions'
import {getIsAuthenticated} from '@vio/core/src/auth/modules/selectors'
import {
  AuthenticationFlows,
  SourceComponentType
} from '@vio/core/src/auth/types/Auth'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {getLanguageCode} from '@vio/core/src/localization/services/locale'
import {BOFH_PROXY_PROVIDER_CODE} from '@vio/core/src/offer/business/isBofhOffer'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {useLogIn} from '@vio/shared/src/authentication/hooks/useLogIn'
import {ComponentSource} from '@vio/shared/src/dealFreeze/types'
import {shouldShowFreezeIcon} from '@vio/shared/src/dealFreeze/utils'
import {
  SelectedOffer,
  writeDealFreezeStash
} from '@vio/shared/src/dealFreeze/utils/stash'
import {DF_FLOW_PARAM} from '@vio/shared/src/dealFreeze/utils/url/constants'
import {Price} from '@vio/shared/src/search/Price'
import {BrandLogo} from '@vio/shared/src/whiteLabel/BrandLogo/BrandLogo'

import {ExitPopupContent} from './ExitPopupContent'
import {ExitPopupHeader} from './ExitPopupHeader'
import {PopupOfferContainer} from './styles'

const REFREEZE_WIDTH = 489

// wrap "Freeze deal" to two lines, which doesn't match Figma.
const CtaLabel = styled.span`
  white-space: nowrap;
`

// BrandLogo's inner <img> has `margin: 0 auto` which centers it inside its flex
// slot — we want it left-aligned in the footer row.
const LeftAlignedBrandLogo = styled(BrandLogo)`
  margin: 0;
`

// Atlas PopUp wraps its content in an outer overlay div that has a default
// `padding: s800` (40px) — fine on desktop, but on mobile it eats viewport
// space and makes the popup feel cramped. Mobile Figma has the popup edge-to-edge
// (modulo the inner Content margin), so we zero the outer padding under desktopXs.
const FullBleedMobilePopUp = styled(PopUp)`
  > div:first-of-type {
    padding: 0;
  }
  ${linariaMq.desktopXs} {
    > div:first-of-type {
      padding: ${cssTheme.layout.spacing.s800};
    }
  }
`

interface Props {
  hotel: HotelType
  offer: SearchOffer
  closePopup: () => void
  dismissPopup: () => void
}

export const DealFreezeExitPopup: React.FC<Props> = ({
  hotel,
  offer,
  closePopup,
  dismissPopup
}) => {
  const isAuthenticated = useSelector(getIsAuthenticated)
  const language = useSelector(getLanguageCode)
  const {brand} = useBrand()
  const {checkIn, checkOut, rooms} = useSelector(getSearchParameters)
  // checkIn/checkOut still needed for the AP URL + stash; not displayed in the subtitle.
  const logIn = useLogIn()
  const showFreezeIcon = shouldShowFreezeIcon(language)

  const onCtaClick = () => {
    // Stash a *minimal* pointer for AP's useResumableDealFreeze. We deliberately
    // skip `roomOffer`, `room`, and `analyticsContext` — AP has fresher,
    // authoritative versions (offer + room via rooms query, analytics via
    // getRoomOfferAnalyticsContexts), and using those handles the case where the
    // offer is no longer available by the time the user lands on AP. `hotel` and
    // `searchDetail` are still stashed as a backstop; AP could derive them too,
    // but stashing them keeps the resume hook resilient to AP-state edge cases.
    writeDealFreezeStash({
      hotel: hotel as unknown as SelectedOffer['hotel'],
      searchDetail: {checkIn, checkOut, roomsSplit: rooms},
      component: ComponentSource.SrpExitPopup,
      offerId: offer.id,
      leadContextRequiredData: {
        trackProviderRedirect,
        providerRateType: offer.metadata?.providerRateType,
        intermediaryProvider: BOFH_PROXY_PROVIDER_CODE
      }
    })

    const apRelativeUrl = generateAccommodationPageURL(
      hotel.objectID,
      language,
      {checkIn, checkOut, rooms, [DF_FLOW_PARAM]: '1'}
    )
    const apAbsoluteUrl = `${window.location.origin}${apRelativeUrl}`

    // Close popup silently — dismissPopup would (incorrectly) fire a Dismissed event.
    closePopup()

    if (isAuthenticated) {
      navigateTo(apAbsoluteUrl)
    } else {
      logIn({
        anonymousId: getAnonymousId() as string,
        flow: AuthenticationFlows.DealFreeze,
        sourceComponent: SourceComponentType.DealFreezeExitPopup,
        unlockHotelId: hotel.objectID,
        onAuthSuccessLandingUrl: apAbsoluteUrl
      })
    }
  }

  return (
    <FullBleedMobilePopUp
      width={REFREEZE_WIDTH}
      isOpen
      navBarProps={{
        centerContent: <ExitPopupHeader variant="dealFreeze" />,
        centerAligned: true,
        variant: 'inverse'
      }}
      closeAction={dismissPopup}
      footer={
        <PopupOfferContainer>
          <Box
            data-id="DealFreezeExitPopupFooter"
            display="flex"
            flexDirection="column"
            gap="s300"
            paddingLeft="s200"
            paddingRight="s200"
          >
            <Text
              data-id="NoCardRequired"
              align="right"
              colorPath="content.success.secondary"
              variant="labelS"
            >
              <FormattedMessageWrapper
                id="dealFreeze.exitPopupNoCardRequired"
                defaultMessage="No card required. No obligation to book."
              />
            </Text>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap="s300"
            >
              <LeftAlignedBrandLogo brand={brand} height={18} />
              <Box display="flex" alignItems="center" gap="s400">
                <Price
                  value={offer.totalRate}
                  currency={offer.currency}
                  size="lg"
                />
                <Button
                  dataId="DealFreezeExitPopupCta"
                  variant="primary"
                  iconEnd={showFreezeIcon ? <Icon name="Ice" /> : undefined}
                  onClick={onCtaClick}
                >
                  <CtaLabel>
                    <FormattedMessageWrapper
                      id="dealFreeze.freezeDeal"
                      defaultMessage="Freeze deal"
                    />
                  </CtaLabel>
                </Button>
              </Box>
            </Box>
          </Box>
        </PopupOfferContainer>
      }
    >
      <Box paddingTop="s400" paddingLeft="s600" paddingRight="s600">
        <Text
          data-id="DealFreezeExitPopupSubtitle"
          variant="bodyS"
          align="center"
        >
          <FormattedMessageWrapper
            id="dealFreeze.exitPopupSubtitle"
            defaultMessage="Secure the room and price. No payment details required."
          />
        </Text>
      </Box>
      <ExitPopupContent
        hotel={hotel}
        paddingTop="s400"
        paddingLeft="s200"
        paddingRight="s200"
      />
    </FullBleedMobilePopUp>
  )
}
